import React, { useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './contexts/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { useAnalytics } from './hooks/useAnalytics';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import FloatingWhatsApp from './components/FloatingWhatsApp';
import Home from './pages/Home';
import './App.css';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Restaurants = React.lazy(() => import('./pages/Restaurants'));
const RealEstate = React.lazy(() => import('./pages/RealEstate'));
const Finance = React.lazy(() => import('./pages/Finance'));
const Advocacy = React.lazy(() => import('./pages/Advocacy'));
const ImportExport = React.lazy(() => import('./pages/ImportExport'));
const Services = React.lazy(() => import('./pages/Services'));
const Blog = React.lazy(() => import('./pages/Blog'));
const BlogPost = React.lazy(() => import('./pages/BlogPost'));
const Contact = React.lazy(() => import('./pages/Contact'));
const Login = React.lazy(() => import('./pages/Login'));
const AdminDashboard = React.lazy(() => import('./pages/AdminDashboard'));
const VisualAdminPanel = React.lazy(() => import('./pages/VisualAdminPanel'));
const PageBuilder = React.lazy(() => import('./pages/PageBuilder'));
const AdminSettings = React.lazy(() => import('./pages/AdminSettings'));
const SiteManager = React.lazy(() => import('./pages/SiteManager'));
const ContentManager = React.lazy(() => import('./pages/ContentManager'));
const VisualEditor = React.lazy(() => import('./pages/VisualEditor'));
const UserProfile = React.lazy(() => import('./pages/UserProfile'));
const PaymentSuccess = React.lazy(() => import('./pages/PaymentSuccess'));
const PaymentCancel = React.lazy(() => import('./pages/PaymentCancel'));

function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash;
    if (hash && hash.includes('session_id=')) {
      const sessionId = hash.split('session_id=')[1]?.split('&')[0];
      if (sessionId) {
        axios.post(`${API}/auth/google/session`, { session_id: sessionId }, { withCredentials: true })
          .then(({ data }) => {
            navigate('/admin', { state: { user: data }, replace: true });
          })
          .catch((error) => {
            console.error('Auth error:', error);
            navigate('/login', { replace: true });
          });
      }
    }
  }, [location, navigate]);

  return <div className="min-h-screen flex items-center justify-center"><p>Authenticating...</p></div>;
}

function AppRouter() {
  const location = useLocation();
  
  // Track page views for analytics
  useAnalytics();
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <div className="App">
      <Navbar />
      <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center"><p>Loading...</p></div>}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/restaurants" element={<Restaurants />} />
          <Route path="/real-estate" element={<RealEstate />} />
          <Route path="/finance" element={<Finance />} />
          <Route path="/advocacy" element={<Advocacy />} />
          <Route path="/import-export" element={<ImportExport />} />
          <Route path="/services" element={<Services />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/login" element={<Login />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/cms" element={<VisualAdminPanel />} />
          <Route path="/admin/page-builder" element={<PageBuilder />} />
          <Route path="/admin/settings" element={<AdminSettings />} />
          <Route path="/admin/site-manager" element={<SiteManager />} />
          <Route path="/admin/content" element={<ContentManager />} />
          <Route path="/admin/visual-editor" element={<VisualEditor />} />
          <Route path="/spbd-admin-secure" element={<AdminDashboard />} />
          <Route path="/spbd-admin-secure/cms" element={<VisualAdminPanel />} />
          <Route path="/spbd-admin-secure/page-builder" element={<PageBuilder />} />
          <Route path="/spbd-admin-secure/settings" element={<AdminSettings />} />
          <Route path="/spbd-admin-secure/site-manager" element={<SiteManager />} />
          <Route path="/spbd-admin-secure/content" element={<ContentManager />} />
          <Route path="/spbd-admin-secure/visual-editor" element={<VisualEditor />} />
          <Route path="/profile" element={<UserProfile />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/payment-cancel" element={<PaymentCancel />} />
        </Routes>
      </React.Suspense>
      <FloatingWhatsApp />
      <Footer />
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
        </AuthProvider>
      </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
